// app/(app)/inventory/units/index.tsx
import { Button, Input } from "@/components/default/forms";
import UnitCard from "@/components/inventory/UnitCard";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearError, fetchUnits } from "@/store/slices/unitSlice";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function UnitsListScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { units, pagination, loading, error } = useAppSelector(
    (state) => state.units,
  );
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const limit = 10;

  useEffect(() => {
    if (error) dispatch(clearError());
    dispatch(fetchUnits({ page: 1, limit, search: searchText }));
  }, [searchText]);

  const loadMore = () => {
    if (!loading && pagination.hasNextPage) {
      dispatch(
        fetchUnits({
          page: pagination.currentPage + 1,
          limit,
          search: searchText,
        }),
      );
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    dispatch(fetchUnits({ page: 1, limit, search: searchText })).finally(() =>
      setRefreshing(false),
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      padding: spacing.lg,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    title: { fontSize: 24, fontWeight: "600", color: colors.text },
    searchContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    searchInput: { flex: 1 },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
  });

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Units</Text>
        <Button
          title="+ Add"
          onPress={() => router.push("/(app)/inventory/units/add")}
          variant="primary"
          containerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}
        />
      </View>
      <View style={styles.searchContainer}>
        <Input
          placeholder="Search by name or symbol"
          value={searchText}
          onChangeText={setSearchText}
          containerStyle={styles.searchInput}
        />
      </View>
      <FlatList
        data={units}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => (
          <UnitCard unit={item} onRefresh={handleRefresh} />
        )}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        ListEmptyComponent={
          !loading ? <Text style={styles.emptyText}>No units found</Text> : null
        }
        ListFooterComponent={
          loading ? (
            <ActivityIndicator
              size="small"
              color={colors.primary}
              style={{ marginVertical: spacing.lg }}
            />
          ) : null
        }
      />
    </View>
  );
}
